/**
 * The page the activation email points at.
 *
 * It exists so that link can finally live on the academy's own domain. Previously the
 * email sent people to the platform host, because the tenant host had no route for
 * `/activate` — it returned this app's shell and quietly did nothing.
 *
 * WHY IT ACTS ON MOUNT RATHER THAN BEHIND A BUTTON. The learner has already expressed
 * intent by clicking a link in their own inbox; asking them to click a second button to
 * confirm what they just asked for is a step that only exists to serve the implementation.
 * The safety that a button would have provided — a scanner cannot activate by prefetching
 * — comes from the endpoint being POST-only instead, so nothing is lost.
 *
 * FOUR OUTCOMES, ALL OF THEM ORDINARY. Activated, already active, a bad key, and still
 * working. "Already active" is deliberately NOT an error: re-opening the email or a mail
 * client fetching the link first are both common, and greeting someone with a failure for
 * doing nothing wrong is the worse mistake.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Icon, Spinner } from '../../shared/ui';
import { activateAccount, type ActivationStatus } from '../../shared/lib/activation';
import './account.css';

type State = ActivationStatus | 'working';

export default function ActivateAccount() {
  const { key = '' } = useParams();
  const [state, setState] = useState<State>('working');

  useEffect(() => {
    let cancelled = false;
    if (!key) {
      setState('invalid');
      return;
    }
    activateAccount(key).then((status) => {
      // The component can unmount before the request settles — a learner who taps back,
      // or a double-render in development. Setting state then is a warning in the console
      // and, worse, a result nobody is looking at.
      if (!cancelled) setState(status === 'pending' ? 'invalid' : status);
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  if (state === 'working') {
    return (
      <main className="activate-page">
        <Spinner label="Activating your account…" />
      </main>
    );
  }

  if (state === 'invalid') {
    return (
      <main className="activate-page">
        <Icon name="error" className="activate-icon activate-icon--bad" />
        <h1 className="activate-title">We couldn&rsquo;t activate this link</h1>
        <p className="activate-note">
          The link may have been copied incompletely, or the account may have been removed.
          Mail clients sometimes split long links across two lines — check that the whole
          address made it into your browser.
        </p>
        <Link className="activate-action" to="/login">
          Go to sign in
        </Link>
      </main>
    );
  }

  const already = state === 'already_active';
  return (
    <main className="activate-page">
      <Icon name="check_circle" className="activate-icon activate-icon--good" />
      <h1 className="activate-title">
        {already ? 'Your account is already active' : 'Your account is active'}
      </h1>
      <p className="activate-note">
        {already
          ? 'Nothing more to do — you can sign in whenever you like.'
          : 'You can sign in now and start your first course.'}
      </p>
      <Link className="activate-action" to="/login">
        Sign in
      </Link>
    </main>
  );
}
